// Dashboard API client. All requests go to the dashboard backend; the
// witness service is intentionally not contacted from the dashboard
// UI. Auth uses a bearer session token stored in localStorage; the
// token is issued by /auth/callback after we hand the AuthKit
// authorization code to the backend for server-side exchange.

const DEFAULT_BASE_URL = "https://dashboard-api.sequesign.com";

export function getApiBaseUrl(): string {
  // Allow overriding for local dev. Vite's import.meta.env exposes
  // VITE_DASHBOARD_API_URL when set in apps/dashboard/.env.local.
  const fromEnv = (import.meta as unknown as { env?: Record<string, string> }).env
    ?.VITE_DASHBOARD_API_URL;
  return fromEnv ?? DEFAULT_BASE_URL;
}

const SESSION_KEY = "sequesign.session_token";

export function getSessionToken(): string | null {
  return localStorage.getItem(SESSION_KEY);
}

export function setSessionToken(token: string): void {
  localStorage.setItem(SESSION_KEY, token);
}

export function clearSessionToken(): void {
  localStorage.removeItem(SESSION_KEY);
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  // Optional per-field validation messages the backend attaches to some
  // 400s (e.g. POST /v1/templates invalid_profile). Undefined when the
  // error body carried no `details` array. Callers that don't need it
  // (most) simply ignore it.
  readonly details?: string[];
  constructor(status: number, code: string, message: string, details?: string[]) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Raised by fetchEvidenceContent so the receipt detail page can branch
// on the dashboard-api error code (EVIDENCE_NOT_HOSTED, ACTION_NOT_FOUND,
// etc.) without re-parsing the response.
export class EvidenceFetchError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "EvidenceFetchError";
    this.status = status;
    this.code = code;
  }
}

export type FetchedEvidence = {
  // Parsed JSON for rendering.
  content: unknown;
  // The exact bytes R2 returned. Hash verification must run against
  // these bytes, not against a re-serialized copy of `content`: the
  // envelope's evidence_hash is sha256 over the canonical bytes the
  // intermediary stored, and re-stringifying the parsed object would
  // not reproduce them.
  bytes: Uint8Array;
};

// Fetches one action's evidence content from dashboard-api. Sends the
// bearer session token (the dashboard authenticates with a token from
// localStorage, not a cookie). Returns both the parsed content and the
// raw bytes so the caller can verify the hash.
export async function fetchEvidenceContent(
  receiptId: string,
  actionId: string,
  opts: { download?: boolean } = {}
): Promise<FetchedEvidence> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const qs = opts.download ? "?download=1" : "";
  const path = `/receipts/${encodeURIComponent(receiptId)}/evidence/${encodeURIComponent(actionId)}${qs}`;
  const response = await fetch(`${getApiBaseUrl()}${path}`, { headers });
  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // ignore
    }
    const code =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : "request_failed";
    const reason =
      payload && typeof payload === "object" && "reason" in payload
        ? String((payload as { reason: unknown }).reason)
        : response.statusText;
    throw new EvidenceFetchError(response.status, code, reason);
  }
  const bytes = new Uint8Array(await response.arrayBuffer());
  const text = new TextDecoder().decode(bytes);
  const content = JSON.parse(text);
  return { content, bytes };
}

// Raised by the package/envelope download helpers so the detail page can
// branch on the dashboard-api error code (INVALID_FORMAT, RECEIPT_NOT_FOUND,
// PACKAGE_TOO_LARGE, etc.).
export class PackageDownloadError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "PackageDownloadError";
    this.status = status;
    this.code = code;
  }
}

export class EnvelopeDownloadError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "EnvelopeDownloadError";
    this.status = status;
    this.code = code;
  }
}

async function errorFromResponse(response: Response): Promise<{ code: string; reason: string }> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    // ignore
  }
  const code =
    payload && typeof payload === "object" && "error" in payload
      ? String((payload as { error: unknown }).error)
      : "request_failed";
  const reason =
    payload && typeof payload === "object" && "reason" in payload
      ? String((payload as { reason: unknown }).reason)
      : response.statusText;
  return { code, reason };
}

// Downloads the full .sequesign package archive for a receipt. Sends the
// bearer session token (the dashboard authenticates with a token from
// localStorage, not a cookie, so credentials:"include" would not help).
export async function downloadReceiptPackage(
  receiptId: string,
  format: "zip" | "tar.gz"
): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const path = `/receipts/${encodeURIComponent(receiptId)}/package?format=${encodeURIComponent(format)}`;
  const response = await fetch(`${getApiBaseUrl()}${path}`, { headers });
  if (!response.ok) {
    const { code, reason } = await errorFromResponse(response);
    throw new PackageDownloadError(response.status, code, reason);
  }
  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const filenameMatch = disposition.match(/filename="([^"]+)"/);
  const filename = filenameMatch?.[1] ?? `${receiptId}.sequesign.${format}`;
  return { blob, filename };
}

// Downloads just the receipt envelope JSON via the existing endpoint.
export async function downloadReceiptEnvelope(
  receiptId: string
): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = {};
  const token = getSessionToken();
  if (token) headers.authorization = `Bearer ${token}`;
  const path = `/receipts/${encodeURIComponent(receiptId)}/download`;
  const response = await fetch(`${getApiBaseUrl()}${path}`, { headers });
  if (!response.ok) {
    const { code, reason } = await errorFromResponse(response);
    throw new EnvelopeDownloadError(response.status, code, reason);
  }
  const blob = await response.blob();
  return { blob, filename: `receipt_${receiptId}.json` };
}

// Computes the lowercase hex SHA-256 of the given bytes using the Web
// Crypto API. The intermediary stores evidence as canonical bytes and
// the envelope's evidence_hash is sha256(those bytes), so hashing the
// raw response bytes directly reproduces evidence_hash without needing
// the canonicalizer in the browser.
export async function sha256HexOfBytes(bytes: Uint8Array): Promise<string> {
  // The bytes come from response.arrayBuffer(), which is always a real
  // ArrayBuffer; the cast narrows the lib's ArrayBufferLike union so
  // crypto.subtle accepts it.
  const digest = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

async function request<T>(
  path: string,
  options: { method?: string; body?: unknown; auth?: boolean } = {}
): Promise<T> {
  const headers: Record<string, string> = { "content-type": "application/json" };
  if (options.auth) {
    const token = getSessionToken();
    if (token) headers.authorization = `Bearer ${token}`;
  }
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: options.method ?? "GET",
    headers,
    body: options.body ? JSON.stringify(options.body) : undefined
  });
  if (!response.ok) {
    let payload: unknown = null;
    try {
      payload = await response.json();
    } catch {
      // ignore
    }
    const code =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : "request_failed";
    const reason =
      payload && typeof payload === "object" && "reason" in payload
        ? String((payload as { reason: unknown }).reason)
        : response.statusText;
    const details =
      payload &&
      typeof payload === "object" &&
      "details" in payload &&
      Array.isArray((payload as { details: unknown }).details)
        ? (payload as { details: unknown[] }).details.map((d) => String(d))
        : undefined;
    throw new ApiError(response.status, code, reason, details);
  }
  return (await response.json()) as T;
}

export type AuthCallbackResponse = {
  ok: true;
  session_token: string;
  expires_at: number;
  customer: {
    customer_id: string;
    opaque_id: string;
    email: string;
    tier: string;
    account_status: string;
    created_at: string;
    newly_created: boolean;
  };
  api_keys?: { api_key: string; read_only_api_key: string };
};

// Response from POST /auth/signout. logout_url is the AuthKit
// logout endpoint the browser redirects to so the WorkOS cookie
// session is cleared as well. It is null when the dashboard-api
// is not configured against a WorkOS tenant (dev/test setups), in
// which case the frontend just clears local state.
export type SignOutResponse = {
  logout_url: string | null;
};

export type MeResponse = {
  customer: {
    customer_id: string;
    opaque_id: string;
    email: string;
    tier: string;
    pricing_model: string;
    account_status: string;
    created_at: string;
    signature_quota_monthly: number | null;
    receipt_library_quota_bytes: number | null;
  };
  current_period: {
    period_id: string | null;
    signatures_used: number;
    verifications_used: number;
    library_bytes_used: number;
  };
};

export type ApiKey = {
  id: string;
  label: string | null;
  key_class: "write" | "read_only";
  key_prefix: string;
  env: "live" | "test";
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
};

export type ListApiKeysResponse = {
  keys: ApiKey[];
  // Effective active-keys-per-class cap for this account (per-account override
  // or the default). Optional for backward compatibility with older responses.
  key_limit_per_class?: number;
};

export type NewApiKey = {
  id: string;
  api_key: string;
  label: string | null;
  key_prefix: string;
  key_class: "write" | "read_only";
  env: "live" | "test";
  created_at: string;
  note: string;
};

// Per-API-key agent key registry: one API key may register many agent Ed25519
// public keys, and the broker accepts a submitted agent key only if it is in
// this key's active set. A registration carries the sha256: fingerprint shown
// in the UI; the private key never reaches the server.
export type AgentKey = {
  id: string;
  agent_public_key: string;
  agent_key_fingerprint: string;
  registered_at: string;
  // Soft-revoke: null for an active registration.
  revoked_at: string | null;
};

export type ListAgentKeysResponse = {
  agent_keys: AgentKey[];
  // Max active agent keys per API key.
  limit: number;
};

// v0.6 step #4 (vouching): a registered attestation key. identity_proof.ref is
// the platform-signed token the SDK attaches so the verifier reports the leg as
// present_verified.
export type Registration = {
  id: string;
  role: "approver" | "counterparty";
  party_type: "human" | "agent" | null;
  identity: string;
  subject_key_fingerprint: string;
  platform_key_id: string;
  created_at: string;
  // Soft-revoke (0017): null for an active registration.
  revoked_at: string | null;
  identity_proof: { issuer: "sequesign"; ref: string };
};

export type ListRegistrationsResponse = {
  registrations: Registration[];
};

// Phase 5 template library: a workflow template in the account's catalog.
// Lifecycle is private → anchored → published; revoke soft-hides it. The
// profile_hash is computed server-side (JCS canonicalization), never trusted
// from the client. The anchor block is a registration-key-signed proof over
// (template_id, profile_hash, anchored_at), verifiable offline against the
// platform's published registration key.
export type TemplateStatus = "private" | "anchored" | "published";

export type TemplateAnchor = {
  issuer: "sequesign";
  key_id: string;
  signature: string;
  anchored_at: string;
};

export type TemplateSummary = {
  template_id: string;
  profile_id: string;
  profile_hash: string;
  tier: string;
  status: TemplateStatus;
  // True when this template belongs to the authenticated caller. A list can mix
  // the caller's own templates with other customers' published templates, so the
  // UI uses this to offer owner-only controls (anchor / publish / revoke) only on
  // rows the backend would actually let this caller mutate. Always false for an
  // anonymous/public viewer. Optional because a dashboard-api not yet redeployed
  // with this field omits it — the UI treats absent as "assume owned" so it does
  // not hide controls from the owner during a deploy gap (see TemplatesPage).
  owned?: boolean;
  author_signed: boolean;
  anchor: TemplateAnchor | null;
  published_at: string | null;
  created_at: string;
};

// The full record adds the WorkflowProfile document itself and the optional
// stored author vouch. Returned by create and get-one.
export type TemplateFull = TemplateSummary & {
  profile: unknown;
  author_signature: unknown | null;
};

export type ListTemplatesResponse = {
  count: number;
  total: number;
  limit: number;
  offset: number;
  templates: TemplateSummary[];
};

export type CreateTemplateResponse = {
  template: TemplateFull;
  // Present and true when an identical profile (same server-computed hash) was
  // already uploaded — the create is idempotent per (customer, profile_hash).
  idempotent?: boolean;
};

export type RevokeTemplateResponse = {
  template_id: string;
  revoked: boolean;
};

// Publisher namespace (migration 0027). The account's claimed handle, or null if
// unclaimed. Publishing a template requires a namespace and a profile_id scoped
// to it ("<namespace>/<name>").
export type NamespaceState = {
  namespace: string | null;
};

export type RevokeRegistrationResponse = {
  id: string;
  revoked: boolean;
};

export type RevokeApiKeyResponse = {
  id: string;
  revoked_at: string | null;
  revocation_reason: string | null;
};

// Legacy two-key rotation response. The dashboard UI's existing key
// rotation flow still uses /api-keys/rotate; the Phase C rewrite of the
// page will move to the per-key endpoints (rotateApiKey/revokeApiKey).
export type RotateResponse = {
  rotated: "write" | "read_only" | "both";
  api_key?: string;
  read_only_api_key?: string;
  note: string;
};

// The /billing/checkout endpoint returns one of two shapes depending
// on the customer's current state:
//   - free → paid: a Checkout Session URL to redirect to (payment
//     method capture).
//   - paid → higher paid: a direct subscription update; no redirect
//     because Stripe already has the payment method on the existing
//     subscription. The frontend shows an in-dashboard success
//     message instead.
export type CheckoutSessionResponse = {
  checkout_url: string;
  checkout_session_id: string;
  tier: "standard" | "managed_isolated";
  pricing_model: "per_signature" | "flat_rate";
  retention_target: "1y" | "7y" | "indefinite";
  price_lookup_keys: string[];
};

export type SubscriptionUpdatedResponse = {
  subscription_updated: true;
  subscription_id: string;
  tier: "standard" | "managed_isolated";
  pricing_model: "per_signature" | "flat_rate";
  retention_target: "1y" | "7y" | "indefinite";
  price_lookup_keys: string[];
};

export type CheckoutResponse = CheckoutSessionResponse | SubscriptionUpdatedResponse;

export function isSubscriptionUpdated(
  response: CheckoutResponse
): response is SubscriptionUpdatedResponse {
  return (response as SubscriptionUpdatedResponse).subscription_updated === true;
}

export type PortalResponse = {
  portal_url: string;
  portal_session_id: string;
};

export type SubscriptionResponse = {
  tier: string;
  pricing_model: string;
  retention_target: string;
  account_status: string;
  stripe_subscription: {
    id: string;
    status: string;
    current_period_start: number | null;
    current_period_end: number | null;
    cancel_at_period_end: boolean;
  } | null;
};

export type ChargeLineItem = {
  description: string;
  quantity: number;
  unit_amount_cents: number;
  amount_cents: number;
  meter: string;
};

// Receipt library types. ReceiptSummary mirrors the per-row shape
// returned by GET /receipts in dashboard-api; ReceiptEnvelope is the
// parsed AgentActionReceipt JSON returned by GET /receipts/:id. The
// envelope type is intentionally locally declared (rather than imported
// from @sequesign/lib) because the dashboard does not otherwise depend
// on the protocol library; the detail page renders a fixed subset of
// fields and the raw JSON.
export type ReceiptSummary = {
  receipt_id: string;
  chain_id: string;
  created_at: string;
  final_action_count: number | null;
  envelope_schema_version: string;
  envelope_stored: boolean;
  // §3.1 Retention PR 2: per-row retention metadata for the list view.
  // retention_until is either an ISO8601 timestamp or the literal
  // "infinity" sentinel. deleted_at / permanently_deleted_at non-null
  // mean the row is on the tombstone or hard-delete path; the UI
  // renders those states distinctly.
  retention_until: string;
  tier_at_creation: string | null;
  deleted_at: string | null;
  permanently_deleted_at: string | null;
  hold_reason: string | null;
};

export type ReceiptRetention = {
  receipt_id: string;
  retention_until: string;
  tier_at_creation: string | null;
  deleted_at: string | null;
  permanently_deleted_at: string | null;
  hold_reason: string | null;
};

export type RetentionState = {
  default_retention: string;
  tier: string;
  total_active_receipts: number;
  upcoming_prunes_next_7d: number;
  tombstoned_receipts: number;
};

export type RetentionChangeEntry = {
  change_id: string;
  change_type: "bulk_extend" | "bulk_shorten" | "default_change";
  receipts_affected: number;
  new_default: string | null;
  applied_at: string;
  reason: string | null;
};

export type RetentionHistoryResponse = {
  changes: RetentionChangeEntry[];
};

export type RetentionMutationResponse = {
  affected: number;
  change_id: string;
  new_minimum_until?: string;
  new_retention_until?: string;
  new_default?: string;
  apply_to_existing?: boolean;
};

export type ListReceiptsResponse = {
  receipts: ReceiptSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type ReceiptEnvelope = {
  schema_version: string;
  receipt_id: string;
  receipt_mode: "freeform" | "schema_validated" | "profile_constrained";
  agent_id: string;
  agent_public_key?: string;
  task: {
    task_id: string;
    delegator_id: string;
    delegated_at: string;
    policy_context_hash?: string;
  };
  chain: {
    chain_id: string;
    initial_chain_state: string;
    final_chain_state: string;
    sequence_start: number;
  };
  action_record_hashes: string[];
  agent_attestations: Array<{
    sequence: number;
    chain_id: string;
    action_record_hash: string;
    chain_state: string;
    agent_id: string;
    signature: string;
  }>;
  witness_attestations: Array<{
    witness_id: string;
    chain_id: string;
    sequence: number;
    action_record_hash: string;
    chain_state: string;
    witnessed_at: string;
  }>;
  approval_attestations: Array<unknown>;
  counterparty_attestations?: Array<unknown>;
  evidence_references: Array<{
    action_id: string;
    evidence_hash: string;
    evidence_path: string;
    mime_type: string;
    evidence_custody: "sequesign_hosted" | "external_client_managed";
  }>;
  profile?: { profile_id: string; profile_hash: string };
  schema_references?: Array<{ schema_id: string; schema_hash: string }>;
};

export type CurrentPeriodResponse = {
  tier: string;
  pricing_model: string;
  retention_target: string;
  period_start: string | null;
  period_end: string | null;
  closed_at: string | null;
  usage: {
    signatures_used: number;
    verifications_used: number;
    library_bytes_used: number;
  };
  quotas: {
    signature_quota_monthly: number | null;
    receipt_library_quota_bytes: number | null;
  };
  estimated_charges: {
    total_cents: number;
    line_items: ChargeLineItem[];
  };
};

export const api = {
  // Hand the AuthKit authorization code to the backend. The backend
  // exchanges it for the user identity (server-side, where there's no
  // CORS), creates/finds the Sequesign customer, and returns a session
  // token + (on first auth) the one-time API keys.
  authCallback: (body: { code: string; code_verifier: string }) =>
    request<AuthCallbackResponse>("/auth/callback", { method: "POST", body }),
  // Server-side sign-out. Marks the session row revoked, calls
  // WorkOS revokeSession, and returns the AuthKit logout URL the
  // browser should redirect to. The frontend calls this BEFORE
  // clearing the local session token so the Bearer header still
  // resolves to the active session at the server.
  signOut: () =>
    request<SignOutResponse>("/auth/signout", {
      method: "POST",
      auth: true
    }),
  me: () => request<MeResponse>("/me", { auth: true }),
  listApiKeys: (opts?: { includeRevoked?: boolean }) => {
    const qs = opts?.includeRevoked ? "?include_revoked=true" : "";
    return request<ListApiKeysResponse>(`/api-keys${qs}`, { auth: true });
  },
  createApiKey: (args: {
    label: string;
    key_class: "write" | "read_only";
    env: "live" | "test";
    // Optional canonical SPKI PEM to enroll an initial agent public key into
    // the new key's registry (a convenience; more can be added afterward via
    // enrollAgentKey). Omitted to create a key with no registered agent keys.
    agent_public_key?: string;
  }) =>
    request<NewApiKey>("/api-keys", {
      method: "POST",
      auth: true,
      body: args
    }),
  // Per-API-key agent key registry. Keypairs are generated/parsed in-browser
  // (lib/keygen.ts, lib/pem.ts); only the public key PEM is submitted.
  listAgentKeys: (apiKeyId: string, opts?: { includeRevoked?: boolean }) => {
    const qs = opts?.includeRevoked ? "?include_revoked=true" : "";
    return request<ListAgentKeysResponse>(
      `/api-keys/${encodeURIComponent(apiKeyId)}/agent-keys${qs}`,
      { auth: true }
    );
  },
  enrollAgentKey: (apiKeyId: string, agent_public_key: string) =>
    request<AgentKey>(`/api-keys/${encodeURIComponent(apiKeyId)}/agent-keys`, {
      method: "POST",
      auth: true,
      body: { agent_public_key }
    }),
  revokeAgentKey: (apiKeyId: string, registrationId: string) =>
    request<{ ok: true }>(
      `/api-keys/${encodeURIComponent(apiKeyId)}/agent-keys/${encodeURIComponent(registrationId)}/revoke`,
      { method: "POST", auth: true }
    ),
  rotateApiKey: (id: string) =>
    request<NewApiKey>(`/api-keys/${encodeURIComponent(id)}/rotate`, {
      method: "POST",
      auth: true
    }),
  revokeApiKey: (id: string, reason?: string) =>
    request<RevokeApiKeyResponse>(`/api-keys/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
      auth: true,
      body: reason ? { reason } : {}
    }),
  rotateApiKeys: (scope: "write" | "read_only" | "both") =>
    request<RotateResponse>("/api-keys/rotate", {
      method: "POST",
      auth: true,
      body: { scope }
    }),
  // v0.6 vouching: list the account's registered attestation keys, and enroll a
  // new one. The proof-of-possession signature is produced in-browser
  // (lib/registration-crypto.ts); the private key never reaches the server.
  listRegistrations: (opts?: { includeRevoked?: boolean }) => {
    const qs = opts?.includeRevoked ? "?include_revoked=true" : "";
    return request<ListRegistrationsResponse>(`/registrations${qs}`, { auth: true });
  },
  revokeRegistration: (id: string, reason?: string) =>
    request<RevokeRegistrationResponse>(`/registrations/${encodeURIComponent(id)}/revoke`, {
      method: "POST",
      auth: true,
      body: reason ? { reason } : {}
    }),
  createRegistration: (body: {
    role: "approver" | "counterparty";
    party_type?: "human" | "agent";
    identity: string;
    public_key: string;
    subject_signature: string;
  }) => request<Registration>("/registrations", { method: "POST", auth: true, body }),
  billingCheckout: (body: {
    tier: "standard" | "managed_isolated";
    pricing_model?: "per_signature" | "flat_rate";
    // Accepted for backward compatibility, ignored server-side. Library
    // storage is one flat price; retention is controlled via pruning
    // policies, not by the subscription line items.
    retention_target?: "1y" | "7y" | "indefinite";
  }) =>
    request<CheckoutResponse>("/billing/checkout", {
      method: "POST",
      auth: true,
      body
    }),
  billingPortal: () => request<PortalResponse>("/billing/portal", { method: "POST", auth: true }),
  billingSubscription: () => request<SubscriptionResponse>("/billing/subscription", { auth: true }),
  billingCurrentPeriod: () =>
    request<CurrentPeriodResponse>("/billing/current-period", { auth: true }),
  listReceipts: (opts: { page: number; pageSize: number }) => {
    const offset = (opts.page - 1) * opts.pageSize;
    const qs = `?limit=${opts.pageSize}&offset=${offset}`;
    return request<ListReceiptsResponse>(`/receipts${qs}`, { auth: true });
  },
  getReceipt: (id: string) =>
    request<ReceiptEnvelope>(`/receipts/${encodeURIComponent(id)}`, { auth: true }),
  listWitnessLog: (opts: {
    page: number;
    pageSize: number;
    apiKeyId?: number;
    since?: string;
    until?: string;
  }) => {
    const offset = (opts.page - 1) * opts.pageSize;
    const params = new URLSearchParams();
    params.set("limit", String(opts.pageSize));
    params.set("offset", String(offset));
    if (opts.apiKeyId !== undefined) params.set("api_key_id", String(opts.apiKeyId));
    if (opts.since) params.set("since", opts.since);
    if (opts.until) params.set("until", opts.until);
    return request<ListWitnessLogResponse>(`/witness-log?${params.toString()}`, {
      auth: true
    });
  },
  getWitnessLogEntry: (position: number) =>
    request<GetWitnessLogEntryResponse>(`/witness-log/${position}`, { auth: true }),
  // §3.1 Retention PR 2: customer-facing retention API. The bulk and
  // default endpoints write retention_changes; the per-receipt extend
  // does not (the row's retention_until is the record).
  getRetentionState: () => request<RetentionState>("/retention", { auth: true }),
  getRetentionHistory: () =>
    request<RetentionHistoryResponse>("/retention/history", { auth: true }),
  extendAllReceipts: (body: { new_minimum_until: string; reason?: string }) =>
    request<RetentionMutationResponse>("/retention/extend-all", {
      method: "POST",
      auth: true,
      body
    }),
  // PR 13.7 (retention safeguards): the shortenAllReceipts client
  // method was removed alongside the /retention/shorten-all route.
  // Customers shorten the policy via changeRetentionDefault, which
  // is forward-effective and never moves an existing receipt's
  // retention_until earlier. Customers who want specific receipts
  // removed sooner use the support-mediated deletion path.
  changeRetentionDefault: (body: {
    new_default: string;
    apply_to_existing?: boolean;
    reason?: string;
  }) =>
    request<RetentionMutationResponse>("/retention/default", {
      method: "PUT",
      auth: true,
      body
    }),
  extendReceipt: (
    receiptId: string,
    body: { until?: string; duration?: string; reason?: string }
  ) =>
    request<{ receipt_id: string; retention_until: string }>(
      `/receipts/${encodeURIComponent(receiptId)}/extend`,
      { method: "POST", auth: true, body }
    ),
  getReceiptRetention: (receiptId: string) =>
    request<ReceiptRetention>(`/receipts/${encodeURIComponent(receiptId)}/retention`, {
      auth: true
    }),
  // §3.1 Retention PR 3 / PR 13.5: customer-initiated account
  // deletion. The server requires a literal confirmation string; the
  // dashboard surfaces it to the customer so the typed value must
  // match.
  //
  // The per-receipt scoped variant (POST /retention/delete with
  // scope=receipts) is intentionally NOT exposed here. PR 13 shipped
  // a self-service per-receipt delete control; PR 13.5 removed it
  // from the customer UI per
  // docs/retention-deletion-accountability-model.md. The backend
  // handler still serves the scope=receipts path and its tests still
  // run, but no customer-facing client method calls it; a future
  // operator-gated path (CLI or admin surface) is the only intended
  // re-entry point.
  deleteAccount: (confirmation: string) =>
    request<RetentionDeleteResponse>("/retention/delete", {
      method: "POST",
      auth: true,
      body: { scope: "account", confirmation }
    }),
  // Phase 5 template library (/v1/* routes). Authenticated list returns the
  // account's own templates (any status) plus everyone's published ones; the
  // status/tier/profile_id filters and limit/offset paginate the catalog.
  listTemplates: (opts?: {
    status?: TemplateStatus;
    tier?: string;
    profileId?: string;
    limit?: number;
    offset?: number;
  }) => {
    const params = new URLSearchParams();
    if (opts?.status) params.set("status", opts.status);
    if (opts?.tier) params.set("tier", opts.tier);
    if (opts?.profileId) params.set("profile_id", opts.profileId);
    if (opts?.limit !== undefined) params.set("limit", String(opts.limit));
    if (opts?.offset !== undefined) params.set("offset", String(opts.offset));
    const qs = params.toString();
    return request<ListTemplatesResponse>(`/v1/templates${qs ? `?${qs}` : ""}`, { auth: true });
  },
  getTemplate: (templateId: string) =>
    request<{ template: TemplateFull }>(`/v1/templates/${encodeURIComponent(templateId)}`, {
      auth: true
    }),
  // Create a private template from a WorkflowProfile document. On an invalid
  // profile the server returns 400 invalid_profile with per-field messages in
  // ApiError.details.
  createTemplate: (profile: unknown, authorSignatureCoseB64url?: string) =>
    request<CreateTemplateResponse>("/v1/templates", {
      method: "POST",
      auth: true,
      body: authorSignatureCoseB64url
        ? { profile, author_signature: { cose_sign1_b64url: authorSignatureCoseB64url } }
        : { profile }
    }),
  // Anchor a private template (registration-key-signed proof), optionally
  // publishing it in the same call. Anchoring is idempotent; publishing the
  // same bytes as an already-published template returns 409 already_published.
  anchorTemplate: (templateId: string, publish?: boolean) =>
    request<{ template: TemplateFull }>("/v1/template-anchors", {
      method: "POST",
      auth: true,
      body: publish ? { template_id: templateId, publish: true } : { template_id: templateId }
    }),
  revokeTemplate: (templateId: string) =>
    request<RevokeTemplateResponse>(`/v1/templates/${encodeURIComponent(templateId)}/revoke`, {
      method: "POST",
      auth: true
    }),
  // Publisher namespace (migration 0027). Read the account's claimed handle, or
  // claim/rename it. POST (not PUT) because the dashboard-api CORS allow-methods
  // is "GET, POST, OPTIONS" — the backend route accepts both, but a browser
  // preflight only permits POST. Errors surface via ApiError.code:
  // invalid_namespace (400), namespace_taken (409), namespace_locked (409).
  getNamespace: () => request<NamespaceState>("/v1/namespace", { auth: true }),
  setNamespace: (namespace: string) =>
    request<{ namespace: string }>("/v1/namespace", {
      method: "POST",
      auth: true,
      body: { namespace }
    })
};

export type RetentionDeleteResponse = {
  scope: "receipts" | "account";
  receipts_deleted: number;
  log_entries_scrubbed: number;
  r2_objects_deleted: number;
};

// The required account-deletion confirmation literal. Mirrors
// DELETE_ACCOUNT_CONFIRMATION in apps/dashboard-api/src/handlers/
// retention.ts. The per-receipt prefix lived here in PR 13 and was
// removed in PR 13.5 along with the self-service per-receipt
// delete control.
export const DELETE_ACCOUNT_CONFIRMATION = "DELETE MY ACCOUNT";

// Witness log view types. Mirror the dashboard-api response shapes
// produced by apps/dashboard-api/src/handlers/witness-log.ts. Kept
// local rather than imported across packages so the dashboard stays
// independent of the backend's compilation graph.
export type WitnessLogSummary = {
  position: number;
  log_id: string;
  chain_id: string | null;
  entry_hash: string;
  key_id: string;
  api_key_id: number | null;
  api_key_prefix: string | null;
  created_at: string;
  batch_id: number | null;
  receipt_id: string | null;
  // Set when this entry's receipt evidence was erased from the platform; the
  // list renders a distinct "deleted" state for it instead of "direct mode".
  evidence_redacted_at: string | null;
};

export type ListWitnessLogResponse = {
  entries: WitnessLogSummary[];
  total: number;
  limit: number;
  offset: number;
};

export type WitnessLogEntryDetail = {
  position: number;
  log_id: string;
  chain_id: string | null;
  chain_state: string | null;
  entry_hash: string;
  previous_entry_hash: string | null;
  witness_signature: string;
  key_id: string;
  api_key_id: number | null;
  api_key_prefix: string | null;
  created_at: string;
  batch_id: number | null;
  receipt_schema_version: string | null;
  // v0.6 step #3b.1: entry kind + satellite hash preimage (null for chain
  // extensions), so a satellite entry is recomputable against its entry_hash.
  entry_kind: "chain_extension" | "satellite";
  attested_receipt_hash: string | null;
  satellite_content_hash: string | null;
};

export type WitnessLogChainStatus = {
  chain_id: string | null;
  has_stored_receipt: boolean;
  receipt_id: string | null;
  total_entries_in_chain: number;
  // Set when this chain's receipt + evidence were erased from the platform;
  // the detail view renders "deleted from platform" instead of "direct mode".
  evidence_redacted_at: string | null;
};

export type WitnessLogBatchStatus =
  | {
      batched: true;
      batch_id: number;
      batch_sealed_at: string;
      merkle_root: string;
      tree_size: number;
      witness_signature: string;
    }
  | { batched: false; estimated_seal_in_seconds: number | null };

export type GetWitnessLogEntryResponse = {
  entry: WitnessLogEntryDetail;
  chain_status: WitnessLogChainStatus;
  batch_status: WitnessLogBatchStatus;
};
